import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  Controls,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import CustomNode from './reactFlowNode.js';
import CustomEdge from './reactFlowEdge.js';
import NodeContentModal from './NodeContentModal';
import engine from './LayoutEngine/engine.js';
import './TreeViewer.scss';

const nodeTypes = { customNode: CustomNode };
const edgeTypes = { customEdge: CustomEdge };


const TreeViewerInner = ({ treeData, visaName, visaId, onClose }) => {
  const [nodes, setNodes, onNodesChangeBase] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showOutcomeDetails, setShowOutcomeDetails] = useState(false);
  const [childrenMap, setChildrenMap] = useState({});
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

  // Build layout on mount / treeData change
  useEffect(() => {
    const { positions, children, edges: rfEdges } = engine(treeData);
    setChildrenMap(children);

    const rfNodes = Object.entries(positions).map(([nodeId, pos]) => {
      const node = treeData.nodes[nodeId] || treeData.nodes[Number(nodeId)];
      const hasChildren = !!(children[nodeId]?.length > 0);
      return {
        id: nodeId,
        type: 'customNode',
        position: pos,
        data: { node, hasChildren, onNodeClick: setSelectedNode },
      };
    });

    setNodes(rfNodes);
    setEdges(rfEdges);
  }, [treeData, setNodes, setEdges]);
  
  // Recalculate edges based on current node positions (delegated to engine)
  const recalculateEdges = useCallback((currentNodes) => {
    const { edges: edgePaths } = engine(currentNodes, childrenMap);
    setEdges(edgePaths);
  }, [childrenMap, setEdges]);

  // Recalculate edges whenever nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      recalculateEdges(nodes);
    }
  }, [nodes, recalculateEdges]);

  // Tree stats
  const { questionCount, outcomeCount, outcomeTypes } = useMemo(() => {
    const nodeIds = Object.keys(treeData.nodes);

    const qCount = nodeIds.filter((nid) => {
      const node = treeData.nodes[nid];
      return node.type !== 'ROOT_NODE' && childrenMap[nid]?.length > 0;
    }).length;

    const outcomes = nodeIds
      .filter((nid) => {
        const node = treeData.nodes[nid];
        return node.type !== 'ROOT_NODE' && !(childrenMap[nid]?.length > 0);
      })
      .map((nid) => treeData.nodes[nid]);

    const types = outcomes.reduce((acc, node) => {
      const content = `${(node.title || '').toLowerCase()} ${(node.text || '').toLowerCase()}`;
      if (/eligible|qualify|approved/.test(content)) acc.eligible = (acc.eligible || 0) + 1;
      else if (/full assessment required|manual/.test(content)) acc.manual = (acc.manual || 0) + 1;
      else if (/furth(e|u)r assessment|continue.*assessment/.test(content)) acc.continueAssessment = (acc.continueAssessment || 0) + 1;
      return acc;
    }, {});

    return { questionCount: qCount, outcomeCount: outcomes.length, outcomeTypes: types };
  }, [treeData, childrenMap]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedNode) {
        if (e.key === 'Escape') setSelectedNode(null);
        return;
      }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, onClose]);

 const handleCloseModal =() => {
    setSelectedNode(null);
  };

  const onEdgeMouseEnter = useCallback((_, edge) => setHoveredEdgeId(edge.id), []);
  const onEdgeMouseLeave = useCallback(() => setHoveredEdgeId(null), []);

  // Derive display nodes/edges with hover-highlight overlay
  const hoveredEdgeMeta = useMemo(
    () => (hoveredEdgeId ? edges.find((e) => e.id === hoveredEdgeId) : null),
    [hoveredEdgeId, edges]
  );

  const displayNodes = useMemo(() => {
    if (!hoveredEdgeMeta) return nodes;
    const connected = new Set([hoveredEdgeMeta.source, hoveredEdgeMeta.target]);
    return nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        opacity: connected.has(n.id) ? 1 : 0.2,
        transition: 'opacity 0.15s ease',
      },
    }));
  }, [nodes, hoveredEdgeMeta]);

  const displayEdges = useMemo(() => {
    if (!hoveredEdgeId) return edges;
    return edges.map((e) => {
      const isHovered = e.id === hoveredEdgeId;
      return {
        ...e,
        style: { ...e.style, strokeWidth: isHovered ? 3.5 : 2, opacity: isHovered ? 1 : 0.1 },
        labelStyle: isHovered ? { ...e.labelStyle, fontWeight: 700 } : { ...e.labelStyle },
        labelBgStyle: isHovered
          ? { ...e.labelBgStyle, strokeWidth: 2.5 }
          : { ...e.labelBgStyle, opacity: 0.1 },
      };
    });
  }, [edges, hoveredEdgeId]);

  const fitViewOptions = useMemo(
    () => ({
      padding: 0.15,
      maxZoom: nodes.length <= 1 ? 1 : 3,
    }),
    [nodes.length]
  );

  return (
    <div className="tree-viewer">
      {/* Header */}
      <div className="tree-viewer-header">
        <div className="header-left">
          <button className="back-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <div className="tree-title">
            <h2>{visaName}</h2>
            <span className="tree-subtitle">Existing Visa ID: {visaId}</span>
          </div>
        </div>
        <div className="header-right">
          <div className="header-stat">
            <span className="stat-label">Questions</span>
            <span className="stat-value">{questionCount}</span>
          </div>
          <div className="header-outcomes-card">
            <div className="header-outcomes-main">
              <span className="header-outcomes-label">Outcomes</span>
              <span className="header-outcomes-value">{outcomeCount}</span>
              {outcomeCount > 0 && (
                <button
                  type="button"
                  className={`header-outcome-toggle ${showOutcomeDetails ? 'active' : ''}`}
                  onClick={() => setShowOutcomeDetails((prev) => !prev)}
                  aria-expanded={showOutcomeDetails}
                  title="Show outcome details"
                >
                  {showOutcomeDetails ? 'Hide details' : 'Details'}
                  <span className="toggle-chevron">{showOutcomeDetails ? '▴' : '▾'}</span>
                </button>
              )}
            </div>

            {outcomeCount > 0 && showOutcomeDetails && (
              <div className="header-outcome-breakdown">
                {outcomeTypes.eligible > 0 && (
                  <span className="outcome-type eligible" title="Eligible outcomes">
                    <span className="outcome-icon">✓</span>
                    <span className="outcome-count">{outcomeTypes.eligible}</span>
                    <span className="outcome-label">Eligible</span>
                  </span>
                )}
                {outcomeTypes.manual > 0 && (
                  <span className="outcome-type manual" title="Manual, full assessment">
                    <span className="outcome-icon">•</span>
                    <span className="outcome-count">{outcomeTypes.manual}</span>
                    <span className="outcome-label">Manual (Full Assessment)</span>
                  </span>
                )}
                {outcomeTypes.continueAssessment > 0 && (
                  <span className="outcome-type continue" title="Continue, full assessment required">
                    <span className="outcome-icon">→</span>
                    <span className="outcome-count">{outcomeTypes.continueAssessment}</span>
                    <span className="outcome-label">Continue (Full Assessment Required)</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div className="tree-canvas-container">
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={onNodesChangeBase}
          onEdgesChange={onEdgesChange}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={fitViewOptions}
          minZoom={0.1}
          maxZoom={3}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <NodeContentModal
        isOpen={!!selectedNode}
        toggle={handleCloseModal}
        node={selectedNode}
        hasChildren={
          selectedNode
            ? !!(childrenMap[selectedNode.id] || childrenMap[String(selectedNode.id)])
            : false
        }
      />
    </div>
  );
};

const TreeViewer = (props) => (
  <ReactFlowProvider>
    <TreeViewerInner {...props} />
  </ReactFlowProvider>
);

export default TreeViewer;